import React from 'react';

const MatchResultBanner = ({ matchResult, onViewCreditNote }) => {
  if (!matchResult) {
    return null;
  }

  const { matchStatus, matchLines, journalEntry, creditNote } = matchResult;
  const isMatched = matchStatus === 'matched';

  return (
    <div className="space-y-4">
      {/* Main Banner */}
      <div
        className={`border rounded-lg p-4 ${
          isMatched
            ? 'bg-green-50 border-green-200'
            : 'bg-amber-50 border-amber-200'
        }`}
      >
        <div className="flex items-start gap-3">
          {/* Icon */}
          {isMatched ? (
            <div className="flex-shrink-0 text-green-600 text-2xl">✓</div>
          ) : (
            <div className="flex-shrink-0 text-amber-600 text-2xl">⚠</div>
          )}

          {/* Content */}
          <div className="flex-1">
            {isMatched ? (
              <div>
                <h3 className="font-semibold text-green-900 text-sm mb-1">
                  3-Way Match Passed — Draft Journal Entry created. Pending Finance review.
                </h3>
                {journalEntry && (
                  <p className="text-xs text-green-700">
                    JE Ref: <span className="font-mono font-semibold">{journalEntry.entryNumber}</span>
                  </p>
                )}
              </div>
            ) : (
              <div>
                <h3 className="font-semibold text-amber-900 text-sm mb-1">
                  Match Failed — Variance Detected
                </h3>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mismatch Details Table */}
      {!isMatched && matchLines && matchLines.length > 0 && (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-gray-100 border-b border-gray-200">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Product</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-700">Ordered</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-700">Received</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-700">Invoiced</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-700">PO Cost</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-700">Invoice Price</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-700">Qty Var %</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-700">Price Var %</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-700">Status</th>
              </tr>
            </thead>
            <tbody>
              {matchLines.map((line, index) => {
                const bothPass = line.qtyPass && line.pricePass;
                const lineStatus =
                  !line.qtyPass && !line.pricePass
                    ? 'both_fail'
                    : !line.qtyPass
                    ? 'qty_fail'
                    : !line.pricePass
                    ? 'price_fail'
                    : 'pass';

                return (
                  <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-900">
                      {line.productName}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {line.orderedQty}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {line.receivedQty}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {line.invoicedQty}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      ₱{Number(line.poCost || 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      ₱{Number(line.invoicePrice || 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span
                        className={`font-semibold ${
                          line.qtyPass ? 'text-green-700' : 'text-red-700'
                        }`}
                      >
                        {Number(line.qtyVariancePct || 0).toFixed(2)}%
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span
                        className={`font-semibold ${
                          line.pricePass ? 'text-green-700' : 'text-red-700'
                        }`}
                      >
                        {Number(line.priceVariancePct || 0).toFixed(2)}%
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {lineStatus === 'pass' ? (
                        <span className="inline-block px-2 py-1 bg-green-100 text-green-700 rounded text-[10px] font-semibold">
                          ✓ Pass
                        </span>
                      ) : (
                        <span className="inline-block px-2 py-1 bg-red-100 text-red-700 rounded text-[10px] font-semibold">
                          ✗ Fail
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Credit Note Summary */}
      {!isMatched && creditNote && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm text-gray-700 mb-2">
                Draft Credit Note{' '}
                <span className="font-mono font-semibold text-gray-900">
                  {creditNote.creditNoteNumber}
                </span>{' '}
                auto-generated for{' '}
                <span className="font-semibold text-gray-900">
                  ₱{Number(creditNote.totalAmount || 0).toFixed(2)}
                </span>
                . Pending Admin approval.
              </p>
            </div>
            {onViewCreditNote && creditNote._id && (
              <button
                onClick={() => onViewCreditNote(creditNote._id)}
                className="flex-shrink-0 px-4 py-2 border border-teal-600 text-teal-600 rounded-md font-medium hover:bg-teal-50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 text-sm whitespace-nowrap"
              >
                View Credit Note →
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchResultBanner;
